import React from 'react'
import { LuDollarSign } from 'react-icons/lu'
import { FiPlus } from 'react-icons/fi'
import Moment from 'moment-timezone'

import { LabelInfo } from '@components/common/form'

import {
  VendorPricingAgreement,
  VendorPricingAgreementTier,
  VendorPricingAgreementServiceTypes,
  ServiceTypeToString,
} from '@types'

import { colors } from '@constants'

interface VendorPricingAgreementTiersDisplayProps {
  pricingAgreements: VendorPricingAgreement[]
  title: string
  width?: string
  onCreateVendorPricingAgreement: () => void
  editVendorPricingAgreement: (
    vendorPricingAgreement: VendorPricingAgreement,
  ) => void
}

const VendorPricingAgreementTiersDisplay: React.FC<
  VendorPricingAgreementTiersDisplayProps
> = ({
  pricingAgreements,
  title,
  width,
  editVendorPricingAgreement,
  onCreateVendorPricingAgreement,
}) => {
  const renderTier = (tier: VendorPricingAgreementTier, index: number) => {
    return (
      <div
        key={index}
        className="flex flex-col gap-2 w-full rounded-lg p-4"
        style={{ border: `1px solid ${colors.gray200}` }}
      >
        <p
          className="px-2 py-1 rounded-md"
          style={{
            border: `1px solid ${colors.gray200}`,
            width: 'max-content',
          }}
        >
          Tier {index + 1}
        </p>
        <div className="flex flex-row w-full justify-between">
          <LabelInfo
            label="Lower Limit"
            value={`$${tier.lowerLimit.toFixed(2)}`}
          />
          <LabelInfo
            label="Upper Limit"
            value={
              tier.upperLimit ? `$${tier.upperLimit.toFixed(2)}` : 'Unlimited'
            }
          />
          <LabelInfo
            label="Discount %"
            value={`${tier.discountPercentage.toFixed(2)}%`}
          />
        </div>
      </div>
    )
  }

  const renderPricingAgreement = (pricingAgreement: VendorPricingAgreement) => {
    return (
      <div
        className="w-11/12 rounded-md p-4 bg-white"
        style={{ border: `1px solid ${colors.gray200}` }}
        key={pricingAgreement.id}
      >
        <div className="flex flex-row items-center gap-2 justify-between">
          <button
            className="px-2 py-1 rounded-md flex flex-row items-center text-white bg-green-400"
            onClick={() => editVendorPricingAgreement(pricingAgreement)}
          >
            Edit
          </button>
          <p className="text-sm text-gray-500">
            Last updated:{' '}
            {Moment(pricingAgreement.updatedAt).format('MM/DD/YYYY, hh:mm A')}
            {` by ${
              pricingAgreement.lastUpdatedBy
                ? pricingAgreement.lastUpdatedBy === ''
                  ? 'N/A'
                  : pricingAgreement.lastUpdatedBy
                : 'N/A'
            }`}
          </p>
        </div>
        <div className="flex flex-row items-center gap-2 my-2">
          <p
            className={`text-sm px-2 py-1 text-white rounded-md ${
              pricingAgreement.active ? 'bg-green-400' : 'bg-red-400'
            }`}
          >
            {pricingAgreement.active ? 'Active' : 'Inactive'}
          </p>
          {pricingAgreement.serviceTypes.map((serviceType, idx) => {
            const color =
              serviceType === VendorPricingAgreementServiceTypes.CATERING
                ? 'bg-orange-400'
                : 'bg-blue-400'

            return (
              <p
                key={idx}
                className={`text-sm px-2 py-1 text-white rounded-md ${color}`}
              >
                {ServiceTypeToString(serviceType)}
              </p>
            )
          })}
        </div>
        <div className="flex flex-col gap-2 w-full">
          {pricingAgreement.tiers.map((tier, index) => renderTier(tier, index))}
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex flex-col gap-4 border-2 border-gray-300 rounded-md p-4 bg-white justify-center"
      style={{ width }}
    >
      <div className="flex flex-row w-full items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          <LuDollarSign size={30} />
          <p className="text-lg font-bold">{title}</p>
        </div>
        <button
          className="px-2 py-1 rounded-md flex flex-row items-center text-white bg-green-400"
          onClick={onCreateVendorPricingAgreement}
        >
          <FiPlus size={20} className="mr-2" />
          Add Pricing Agreement
        </button>
      </div>
      <div className="flex flex-col gap-2 w-full justify-center items-center">
        {pricingAgreements.map(renderPricingAgreement)}
      </div>
    </div>
  )
}

export default VendorPricingAgreementTiersDisplay
